import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
  TextField,
  Chip,
  Collapse,
  Pagination,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh, ExpandMore, ExpandLess, Person, ArrowUpward, ArrowDownward, History, Delete, AutoAwesome, Psychology, PlayArrow, Search } from '@mui/icons-material';
import { PageHeader, Loading, ErrorMessage, EmptyState, StatusBadge, ScoreChip, ConfirmDialog } from './common';
import { useSubmissions } from '../hooks/useSubmissions';
import { apiService } from '../services/api';

const sortOptions = [
  { value: 'created_at', label: 'Date Created' },
  { value: 'evaluated_total', label: 'Evaluated Total' },
  { value: 'ground_truth_total', label: 'Ground Truth Total' },
];

const EvalTypeBadge = ({ type }) => {
  if (type === 'agentic') {
    return (
      <Chip
        icon={<AutoAwesome fontSize="small" />}
        label="Agentic"
        color="secondary"
        size="small"
        variant="filled"
      />
    );
  }
  return (
    <Chip
      icon={<Psychology fontSize="small" />}
      label="Standard"
      color="default"
      size="small"
      variant="outlined"
    />
  );
};

const SubmissionsList = () => {
  const {
    submissions,
    loading,
    error,
    pagination,
    sortBy,
    sortOrder,
    candidateId,
    setCandidateId,
    refresh,
    changePage,
    changePageSize,
    changeSort,
    searchByCandidateId,
  } = useSubmissions();

  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [evaluateDialogOpen, setEvaluateDialogOpen] = useState(false);
  const [evaluateId, setEvaluateId] = useState('');
  const [evaluating, setEvaluating] = useState(false);

  const [autoEvaluating, setAutoEvaluating] = useState(false);

  const toggleExpanded = (submissionId) => {
    setExpandedSubmission(expandedSubmission === submissionId ? null : submissionId);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.submissions.delete(deleteTarget);
      setDeleteOpen(false);
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      console.error('Error deleting submission:', err);
      alert(err.response?.data?.error || 'Failed to delete submission');
    } finally {
      setDeleting(false);
    }
  };

  const handleEvaluate = async () => {
    if (!evaluateId.trim()) return;
    setEvaluating(true);
    try {
      const res = await apiService.candidates.evaluate({
        candidate_id: parseInt(evaluateId.trim()),
        tool_id: 7,
        language_id: 1,
      });
      alert(res.data.message || `Evaluation started for candidate ${evaluateId}`);
      setEvaluateDialogOpen(false);
      setEvaluateId('');
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to evaluate candidate');
    } finally {
      setEvaluating(false);
    }
  };

  const handleAutoEvaluate = async () => {
    setAutoEvaluating(true);
    try {
      const res = await apiService.candidates.autoEvaluate({
        tool_id: 7,
        language_id: 1,
        max_candidates: 10,
      });
      alert(res.data.message || 'Auto-evaluation started');
      refresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to start auto-evaluation');
    } finally {
      setAutoEvaluating(false);
    }
  };

  const calculateTotalScore = (evaluations) => {
    if (!evaluations || evaluations.length === 0) return 0;
    return evaluations.reduce((total, evaluation) => {
      const score = evaluation.score || 0;
      const isCounterproductive = evaluation.aspect_name &&
        evaluation.aspect_name.toString().toUpperCase().includes('COUNTERPRODUCTIVE');
      return isCounterproductive ? total - score : total + score;
    }, 0);
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  if (loading && submissions.length === 0) {
    return <Loading message="Loading submissions..." />;
  }

  return (
    <Box>
      <PageHeader
        title="Submissions & Evaluations"
        subtitle="Browse and inspect candidate submissions and their evaluation results"
        actions={[
          { label: 'Evaluate', icon: <PlayArrow />, onClick: () => setEvaluateDialogOpen(true), color: 'primary' },
          { label: autoEvaluating ? 'Running...' : 'Auto-Evaluate', icon: <AutoAwesome />, onClick: handleAutoEvaluate, disabled: autoEvaluating },
          { label: 'Refresh', icon: <Refresh />, onClick: refresh, loading },
        ]}
      />

      {error && <ErrorMessage message={error} onRetry={refresh} />}

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Search Candidate ID"
                value={candidateId}
                onChange={(e) => setCandidateId(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchByCandidateId(candidateId); }}
                size="small"
                sx={{ minWidth: 180 }}
                placeholder="Enter candidate ID..."
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => searchByCandidateId(candidateId)}
                startIcon={<Search />}
              >
                Search
              </Button>
              {candidateId && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => searchByCandidateId('')}
                >
                  Clear
                </Button>
              )}
              <TextField
                select
                label="Items per page"
                value={pagination.pageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
                size="small"
                sx={{ minWidth: 120 }}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </TextField>
              <TextField
                select
                label="Sort by"
                value={sortBy}
                onChange={(e) => changeSort(e.target.value, sortOrder)}
                size="small"
                sx={{ minWidth: 180 }}
              >
                {sortOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                size="small"
                onClick={() => changeSort(sortBy, sortOrder === 'desc' ? 'asc' : 'desc')}
                startIcon={sortOrder === 'desc' ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />}
              >
                {sortOrder === 'desc' ? 'Descending' : 'Ascending'}
              </Button>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <strong>{pagination.totalCount}</strong> total submissions
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Submissions */}
      {submissions.length === 0 && !loading ? (
        <EmptyState
          title="No submissions found"
          description="There are no submissions in the system yet."
          actionLabel="Refresh"
          onAction={refresh}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {submissions.map(submission => {
            const isAgentic = submission.evaluation_type === 'agentic';
            return (
              <Card
                key={submission.id}
                variant="outlined"
                sx={{ borderLeft: 4, borderColor: isAgentic ? 'secondary.main' : 'primary.main' }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="h6" fontWeight={700}>
                          {submission.candidate_id ? `Candidate ${submission.candidate_id}` : `Submission ${submission.id}`}
                        </Typography>
                        <EvalTypeBadge type={submission.evaluation_type} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Created: {formatDate(submission.created_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <StatusBadge status={submission.status} />
                      <Typography variant="caption" color="text.secondary">
                        Parse: {submission.parse_status_display || submission.parse_status}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Typography variant="body2">
                      <strong>{submission.evaluations_count}</strong> evaluations completed
                    </Typography>

                    {submission.evaluations && submission.evaluations.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Total Score:</Typography>
                        <ScoreChip score={calculateTotalScore(submission.evaluations)} maxScore={80} />
                      </Box>
                    )}

                    {submission.ground_truth_total !== null && submission.ground_truth_total !== undefined && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">Ground Truth:</Typography>
                        <ScoreChip score={submission.ground_truth_total} maxScore={80} />
                      </Box>
                    )}

                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                      {submission.candidate_id && (
                        <Button
                          component={Link}
                          to={`/candidate-detail/${submission.candidate_id}`}
                          variant="outlined"
                          size="small"
                          startIcon={<History />}
                          color="secondary"
                        >
                          All Runs
                        </Button>
                      )}
                      <Button
                        component={Link}
                        to={`/candidate/${submission.id}`}
                        href={`/candidate/${submission.id}`}
                        variant="contained"
                        size="small"
                        startIcon={<Person />}
                      >
                        View Evaluation
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => toggleExpanded(submission.id)}
                        endIcon={expandedSubmission === submission.id ? <ExpandLess /> : <ExpandMore />}
                      >
                        {expandedSubmission === submission.id ? 'Hide' : 'Show'} Details
                      </Button>
                      <Tooltip title="Delete submission">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteTarget(submission.id);
                            setDeleteOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Collapse in={expandedSubmission === submission.id} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      {submission.evaluations?.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          No evaluations completed yet.
                        </Typography>
                      ) : (
                        <Grid container spacing={2}>
                          {submission.evaluations.map((evaluation, index) => {
                            const isCounterproductive = evaluation.aspect_name &&
                              evaluation.aspect_name.toString().toUpperCase().includes('COUNTERPRODUCTIVE');
                            return (
                              <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card variant="outlined" sx={{ borderLeft: 3, borderColor: isCounterproductive ? 'error.main' : 'primary.main' }}>
                                  <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Typography variant="subtitle2" fontWeight={700}>
                                        {evaluation.aspect_name}
                                        {isCounterproductive && <Chip label="CP" color="error" size="small" sx={{ ml: 0.5 }} />}
                                      </Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {evaluation.ground_truth_score !== null && evaluation.ground_truth_score !== undefined && (
                                          <Typography variant="caption" fontWeight={700} color="success.main">
                                            GT: {evaluation.ground_truth_score.toFixed(1)}
                                          </Typography>
                                        )}
                                        <Typography variant="body2" fontWeight={700} color={isCounterproductive ? 'error.main' : 'primary.main'}>
                                          {isCounterproductive ? '-' : ''}{evaluation.score.toFixed(1)}/2
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Control ID: {evaluation.control_sum_id}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {evaluation.explanation}
                                    </Typography>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={(_, page) => changePage(page)}
            color="primary"
            disabled={loading}
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="Delete Submission"
        message="This will permanently delete this submission and all its evaluation results. This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => { setDeleteOpen(false); setDeleteTarget(null); }}
        confirmLabel={deleting ? 'Deleting...' : 'Delete'}
        confirmDisabled={deleting}
        confirmColor="error"
      />

      {/* Evaluate Candidate Dialog */}
      <ConfirmDialog
        open={evaluateDialogOpen}
        title="Evaluate Candidate"
        message="Enter the candidate ID to start a new evaluation."
        onConfirm={handleEvaluate}
        onCancel={() => { setEvaluateDialogOpen(false); setEvaluateId(''); }}
        confirmLabel={evaluating ? 'Evaluating...' : 'Evaluate'}
        confirmDisabled={evaluating || !evaluateId.trim()}
      >
        <TextField
          label="Candidate ID"
          value={evaluateId}
          onChange={(e) => setEvaluateId(e.target.value)}
          fullWidth
          size="small"
          autoFocus
          sx={{ mt: 2 }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleEvaluate(); }}
        />
      </ConfirmDialog>
    </Box>
  );
};

export default SubmissionsList;